import type { CharacterProfile } from '../types';

export type FightAmbience = 'embers' | 'moths' | 'rain' | 'chickens' | 'sparks';

export interface FightStage {
  id: string;
  name: string;
  image: string;
  pit: boolean;
  ambience: FightAmbience;
  accent: string;
  accentSoft: string;
  motif: string;
  ownerId: string;
}

export type CampaignEncounterKind = 'roster' | 'dark-self' | 'convergence' | 'rage-convergence' | 'cheater-rigged' | 'cheater-final';

export interface CampaignEncounter {
  id: string;
  kind: CampaignEncounterKind;
  opponentId: string;
  stageOwnerId: string;
  title: string;
  speedMultiplier: number;
  aggression: number;
  forcedLoss: boolean;
}

export type FighterArchetype = 'balanced' | 'power' | 'rush' | 'trick' | 'technical' | 'boss';
export type FighterSpecialShape = 'orb' | 'wave' | 'flare' | 'shard' | 'arc' | 'ghost';

export interface FighterCombatProfile {
  archetype: FighterArchetype;
  specialShape: FighterSpecialShape;
  animationSeed: number;
  specialDamage: number;
  specialSpeed: number;
  specialKnockback: number;
  specialHitStun: number;
  specialScale: number;
  specialCooldownMs: number;
}

const namedStages: Record<string, Pick<FightStage, 'name' | 'ambience' | 'pit' | 'motif'>> = {
  absolution: { name: 'Quiet Hollow Cemetery', ambience: 'moths', pit: true, motif: 'THE LAST BELL TOLLS' },
  acorn: { name: "Acorn's Moonlit Scratching Post", ambience: 'moths', pit: false, motif: 'TOP SHELF TERRITORY' },
  alaia: { name: 'Rue de Ruin', ambience: 'rain', pit: false, motif: 'LEFT BANK AFTER HOURS' },
  beastly: { name: 'The Ninth Worksite', ambience: 'sparks', pit: true, motif: 'DEMOLITION AHEAD OF SCHEDULE' },
  candy: { name: "Candy's Island of Corn", ambience: 'embers', pit: false, motif: 'SEASONAL COLOR // PERMANENT DAMAGE' },
  dj: { name: 'Low-Orbit Soundstage', ambience: 'sparks', pit: true, motif: 'DROP THE BEAT // MIND THE ROCKET' },
  'dr-butt-humpest': { name: "Dr. Butt's Operating Theater", ambience: 'moths', pit: true, motif: 'RESEARCH WITHOUT ETHICS' },
  falln: { name: 'Runway Zero', ambience: 'rain', pit: true, motif: 'LANDING OPTIONAL' },
  iicaptain: { name: 'Black Flag Command Deck', ambience: 'sparks', pit: false, motif: 'ORDERS CLASSIFIED' },
  jax: { name: 'Island Zero Cargo Pier', ambience: 'rain', pit: true, motif: 'QUEUE ONE STARTS HERE' },
  pb: { name: 'Northern Neighborhood 82', ambience: 'moths', pit: false, motif: 'A BEAUTIFUL DAY FOR CONSEQUENCES' },
  phantom: { name: 'The Last Candle', ambience: 'moths', pit: true, motif: 'THE DARK IS LISTENING' },
  'playa-h8ter': { name: 'The Velvet Tax Ballroom', ambience: 'embers', pit: false, motif: 'EVERY INSULT COSTS EXTRA' },
  shiv: { name: 'Underpass Six', ambience: 'embers', pit: false, motif: 'FIRE CODE REVOKED' },
  'chicken-owner': { name: "The Chicken Owner's Midnight Farm", ambience: 'chickens', pit: false, motif: 'THE FLOCK KEEPS SCORE' },
  'timbs-and-a-forty': { name: 'Cabinet 40 Service Hall', ambience: 'sparks', pit: true, motif: 'ONE LIFE // BRING QUARTERS' },
  vandal: { name: 'Vandal Signal Rooftop', ambience: 'rain', pit: false, motif: 'MANNERS COST NOTHING' },
  wake: { name: 'Sector Green Quarantine', ambience: 'sparks', pit: true, motif: 'KEEP UP OR QUARANTINE' },
  infirmary: { name: 'The Whiteout Infirmary', ambience: 'moths', pit: false, motif: 'VENTILATION HAS FAILED' },
  'just-a-feller': { name: 'Splinter Point', ambience: 'embers', pit: true, motif: 'JUST CLEARING THE BRACKET' },
  kane: { name: 'The Storm Array', ambience: 'sparks', pit: true, motif: 'THE GRID REMEMBERS' },
  pants: { name: 'The Concrete Locker', ambience: 'moths', pit: false, motif: 'THE BELL APOLOGIZES FIRST' },
  prosper: { name: 'The Lantern Room', ambience: 'moths', pit: false, motif: 'FEAR PAYS INTEREST' },
  savage: { name: 'The Red Maw', ambience: 'embers', pit: true, motif: 'NAMES ARE FOR SURVIVORS' },
  skysong: { name: 'The Wind Bell Terrace', ambience: 'moths', pit: true, motif: 'THE HIGH NOTE FALLS' },
  stargazed: { name: 'The Salt Circuit', ambience: 'rain', pit: true, motif: 'CATCH THE TAILLIGHTS' },
  suwucide: { name: 'The Pink Panic Room', ambience: 'sparks', pit: false, motif: 'WATCH THE HANDS' },
  'the-zombie': { name: 'The Quarantine Pier', ambience: 'rain', pit: true, motif: 'THE ROUND REFUSES TO END' },
  tekponz: { name: 'The Red Arrival Hall', ambience: 'embers', pit: false, motif: 'SHOW THE WORK' },
  'the-cheater': { name: 'The Untouchable Queue', ambience: 'sparks', pit: true, motif: 'PERFECTLY LEGAL INPUT' },
  'the-convergence': { name: 'Island Zero Copy Furnace', ambience: 'embers', pit: true, motif: 'THIRTY-ONE BECOME ONE' },
};

const stageName = (character: CharacterProfile) => namedStages[character.id]?.name ?? character.arena ?? `${character.name}'s Home Ground`;

export function buildFightStages(characters: CharacterProfile[]): FightStage[] {
  return characters.map((character, index) => ({
    id: `stage-${character.id}`,
    ownerId: character.id,
    name: stageName(character),
    image: character.id === 'candy'
      ? '/assets/fighting/stages/candy-island-of-corn.webp'
      : character.images.find((image) => image.kind === 'Cinematic')?.src ?? '/assets/attract/finale/finale-arena-empty.webp',
    pit: namedStages[character.id]?.pit ?? index % 11 === 7,
    ambience: namedStages[character.id]?.ambience ?? (['embers', 'moths', 'rain', 'chickens', 'sparks'] as const)[index % 5],
    motif: namedStages[character.id]?.motif ?? `${character.nickname.toUpperCase()} TERRITORY`,
    accent: character.theme.accent,
    accentSoft: character.theme.accentSoft,
  }));
}

export function buildCampaign(roster: CharacterProfile[], playerId: string): CampaignEncounter[] {
  const playerIndex = Math.max(0, roster.findIndex((character) => character.id === playerId));
  const opponents = Array.from({ length: Math.max(0, roster.length - 1) }, (_, offset) => roster[(playerIndex + offset + 1) % roster.length])
    .filter((character) => character.id !== playerId);
  const rosterFights = opponents.map((opponent, index): CampaignEncounter => ({
    id: `roster-${opponent.id}`,
    kind: 'roster',
    opponentId: opponent.id,
    stageOwnerId: opponent.id,
    title: `ISLAND BRACKET // FIGHT ${String(index + 1).padStart(2, '0')}`,
    speedMultiplier: 1,
    aggression: Math.min(1.8, 0.72 + index * 0.035),
    forcedLoss: false,
  }));
  return [
    ...rosterFights,
    { id: 'dark-self', kind: 'dark-self', opponentId: playerId, stageOwnerId: playerId, title: 'MIRROR BREACH // DARK SELF', speedMultiplier: 1.12, aggression: 1.55, forcedLoss: false },
    { id: 'convergence', kind: 'convergence', opponentId: 'the-convergence', stageOwnerId: 'the-convergence', title: 'ISLAND ZERO // THE CONVERGENCE', speedMultiplier: 1.18, aggression: 1.75, forcedLoss: false },
    { id: 'rage-convergence', kind: 'rage-convergence', opponentId: 'the-convergence', stageOwnerId: 'the-convergence', title: 'ISLAND ZERO // CONVERGENCE RAGE', speedMultiplier: 2, aggression: 2.15, forcedLoss: false },
    { id: 'cheater-rigged-1', kind: 'cheater-rigged', opponentId: 'the-cheater', stageOwnerId: 'the-cheater', title: 'UNTOUCHABLE QUEUE // RIGGED BOUT 1', speedMultiplier: 1.35, aggression: 2.2, forcedLoss: true },
    { id: 'cheater-rigged-2', kind: 'cheater-rigged', opponentId: 'the-cheater', stageOwnerId: 'the-cheater', title: 'UNTOUCHABLE QUEUE // RIGGED BOUT 2', speedMultiplier: 1.55, aggression: 2.45, forcedLoss: true },
    { id: 'cheater-rigged-3', kind: 'cheater-rigged', opponentId: 'the-cheater', stageOwnerId: 'the-cheater', title: 'UNTOUCHABLE QUEUE // RIGGED BOUT 3', speedMultiplier: 1.8, aggression: 2.7, forcedLoss: true },
    { id: 'cheater-final', kind: 'cheater-final', opponentId: 'the-cheater', stageOwnerId: 'the-cheater', title: 'FINAL APPEAL // NO MORE CHEATS', speedMultiplier: 1.25, aggression: 2.25, forcedLoss: false },
  ];
}

export const campaignStorageKey = (fighterId: string) => `smackers-fight-campaign-v2:${fighterId}`;

export const japaneseCalls = ['IKUZO!', 'KURAE!', 'SHOBU!', 'KAKUGO!', 'TOBE!', 'SOKO DA!'] as const;

const stableCharacterSeed = (value: string) => Array.from(value).reduce((seed, character) => ((seed * 31) + character.charCodeAt(0)) >>> 0, 2166136261);

export function combatProfile(character: CharacterProfile): FighterCombatProfile {
  const label = `${character.classification} ${character.tournamentTier ?? ''}`.toLowerCase();
  const archetype: FighterArchetype = character.isBoss || label.includes('boss')
    ? 'boss'
    : label.includes('heavy') || label.includes('bruiser') ? 'power'
      : label.includes('speed') || label.includes('aerial') ? 'rush'
        : label.includes('trick') || label.includes('chaos') || label.includes('wild') ? 'trick'
          : label.includes('technical') || label.includes('genius') || label.includes('mastermind') ? 'technical'
            : 'balanced';
  const animationSeed = stableCharacterSeed(character.id);
  const specialShapes: FighterSpecialShape[] = ['orb', 'wave', 'flare', 'shard', 'arc', 'ghost'];
  const tuning: Record<FighterArchetype, Pick<FighterCombatProfile, 'specialDamage' | 'specialSpeed' | 'specialKnockback' | 'specialHitStun' | 'specialScale' | 'specialCooldownMs'>> = {
    balanced: { specialDamage: 12, specialSpeed: 1.42, specialKnockback: 3.8, specialHitStun: 8, specialScale: 1, specialCooldownMs: 760 },
    power: { specialDamage: 16, specialSpeed: 1.08, specialKnockback: 5.4, specialHitStun: 11, specialScale: 1.28, specialCooldownMs: 940 },
    rush: { specialDamage: 9, specialSpeed: 1.92, specialKnockback: 2.6, specialHitStun: 6, specialScale: .82, specialCooldownMs: 560 },
    trick: { specialDamage: 11, specialSpeed: 1.3, specialKnockback: 4.5, specialHitStun: 10, specialScale: 1.08, specialCooldownMs: 820 },
    technical: { specialDamage: 11, specialSpeed: 1.62, specialKnockback: 3.2, specialHitStun: 9, specialScale: .94, specialCooldownMs: 650 },
    boss: { specialDamage: 18, specialSpeed: 1.48, specialKnockback: 6.2, specialHitStun: 12, specialScale: 1.48, specialCooldownMs: 980 },
  };
  return { archetype, specialShape: specialShapes[animationSeed % specialShapes.length], animationSeed, ...tuning[archetype] };
}

export function finishingMoveNames(character: CharacterProfile) {
  const signature = character.signatureMove.replace(/[.!?]+$/g, '').toUpperCase();
  const nickname = character.nickname.replace(/^THE\s+/i, '').toUpperCase();
  const motif = character.theme.motif.replace(/[-_]+/g, ' ').toUpperCase();
  return [
    signature,
    `${nickname} BREAKER`,
    `${motif} FINAL SMACK`,
  ] as const;
}

export const fightControls = {
  p1: 'A/D move · W jump · S crouch · F punch · G uppercut · H kick · S then toward + F special (Q shortcut) · R block · T run',
  p2: '←/→ move · ↑ jump · ↓ crouch · J punch · K uppercut · L kick · ↓ then toward + J special (O shortcut) · U block · I run',
  controller: 'Left stick/D-pad move · A kick · X punch · Y uppercut · down then toward + X special (B shortcut) · RB block · LB run',
} as const;
